import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MapPin, ChevronRight, Plus, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { psGetConfig } from "../../lib/psApi";
import { usePsStore } from "../../store/psStore";
import { metaFor } from "../../components/ServiceMeta";

export default function PsService() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const { order, ensureOrder, addService, refresh } = usePsStore();
  const [config, setConfig] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    psGetConfig().then(setConfig).catch(() => {});
    (async () => {
      await ensureOrder().catch(() => {});
      await refresh().catch(() => {});
      const wanted = sp.get("s");
      if (wanted) {
        try { await addService(wanted.toUpperCase()); nav(`/personal-shopper/${wanted.toUpperCase()}`, { replace: true }); } catch { /* ignore */ }
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const subs = order?.sub_orders || [];
  const stopCount = order?.stop_count ?? subs.length;
  const maxStops = order?.max_stops ?? config?.max_stops ?? 5;

  const toggle = async (stop) => {
    setBusy(stop);
    try { await addService(stop); nav(`/personal-shopper/${stop}`); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || "Impossible d'ajouter ce service"); }
    finally { setBusy(null); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-2xl font-extrabold" style={{ color: "#0b1220" }}>Personal Shopper</h1>
        <span className="flex items-center gap-1.5 text-sm font-bold px-3 py-1.5 rounded-full" style={{ background: "#f0fdf4", color: "#16a34a" }} data-testid="ps-stop-badge">
          <MapPin size={14} /> {stopCount} / {maxStops} arrêts
        </span>
      </div>
      <p className="text-sm mb-6" style={{ color: "#64748b" }}>Choisissez vos enseignes. Votre shopper fait les courses et tout est livré ensemble.</p>

      <div className="space-y-3">
        {(config?.services || []).map((sv) => {
          const m = metaFor(sv.stop_type, sv.label);
          const Icon = m.Icon;
          const selected = subs.find((s) => s.stop_type === sv.stop_type);
          const disabledAdd = !selected && stopCount >= maxStops;
          return (
            <div key={sv.stop_type} data-testid={`ps-service-card-${sv.stop_type}`}
              onClick={() => (selected ? nav(`/personal-shopper/${sv.stop_type}`) : (!disabledAdd && toggle(sv.stop_type)))}
              className="flex items-center gap-4 bg-white rounded-2xl p-4 cursor-pointer transition-transform hover:-translate-y-0.5"
              style={{ border: selected ? `2px solid ${m.accent}` : "1px solid #f0f0f0", opacity: disabledAdd ? 0.4 : 1 }}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: m.accent + "1A" }}>
                <Icon size={26} color={m.accent} />
              </div>
              <div className="flex-1">
                <p className="font-extrabold" style={{ color: "#0b1220" }}>{sv.label}</p>
                <p className="text-xs mt-0.5" style={{ color: "#64748b" }}>{sv.fulfillment_type === "MERCHANT_PREPARED" ? "Préparé par le marchand" : "Courses en rayon"}</p>
              </div>
              {selected ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold" style={{ color: m.accent }}>{selected.item_count} art.</span>
                  <ChevronRight size={20} color="#94a3b8" />
                </div>
              ) : (
                <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white" style={{ background: m.accent }}>
                  {busy === sv.stop_type ? "…" : <Plus size={22} />}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {subs.length > 0 && (
        <button onClick={() => nav("/personal-shopper/panier")} data-testid="ps-service-goto-cart"
          className="w-full rounded-2xl font-bold text-white py-4 mt-6 flex items-center justify-center gap-2" style={{ background: "#0b1220" }}>
          <ShoppingCart size={20} /> Voir mon panier ({order?.item_count ?? 0})
        </button>
      )}
    </div>
  );
}
